import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import dayjs from "dayjs";
import { SERVER_URL } from "../../constants";
import {
  getStudentsByIds,
  listStaysForStudents,
  getAdultsByIds,
  listNsrsForAdult,
} from "./api/people";
import { fmtDate } from "./utils/dates";
import BespokeEventDialog from "../shared utils/BespokeEventDialog.jsx";
import CopyBookingsDialog from "./CopyBookingsDialog";
import MultiCopyBookingsDialog from "./MultiCopyBookingsDialog";

const d10 = (s) => (s ? String(s).slice(0, 10) : "");
const maxStr = (a, b) => (a > b ? a : b);
const minStr = (a, b) => (a < b ? a : b);
const normCampus = (v) => String(v ?? "").trim().toLowerCase();

const studentName = (s) =>
  `${s.studentName || s.name || ""} ${s.studentSurname || s.surname || ""}`.trim() || `#${s.id}`;
const adultName = (a) =>
  `${a.adultName || a.name || ""} ${a.adultSurname || a.surname || ""}`.trim() || `#${a.id}`;

// Product types: ordinal (as the API sends them) ↔ name, each with a colour and a
// stack priority. Stack order top→bottom: residential on its own rows, then everything
// else (weekend/activities/external/extra/events) packed together below.
const TYPE_BY_ORDINAL = {
  "0": "RESIDENTIAL",
  "1": "AFTERNOON",
  "2": "WEEKEND",
  "3": "EXTERNAL",
  "4": "EXTRA",
  "5": "EVENT",
};
const TYPE_META = {
  RESIDENTIAL: { rank: 0, label: "Residential", color: "#e3f2fd", border: "#1976d2" },
  WEEKEND: { rank: 1, label: "Weekend", color: "#e8f5e9", border: "#2e7d32" },
  AFTERNOON: { rank: 1, label: "Activity", color: "#f3e5f5", border: "#7b1fa2" },
  EXTERNAL: { rank: 1, label: "External", color: "#fff3e0", border: "#ef6c00" },
  EXTRA: { rank: 1, label: "Extra", color: "#e0f7fa", border: "#00838f" },
  EVENT: { rank: 1, label: "Event", color: "#fce4ec", border: "#c2185b" },
};
const LEGEND_ORDER = ["RESIDENTIAL", "WEEKEND", "AFTERNOON", "EXTERNAL", "EXTRA", "EVENT"];
const typeKey = (pt) => {
  const s = String(pt ?? "").trim().toUpperCase();
  if (TYPE_META[s]) return s;
  if (TYPE_BY_ORDINAL[s]) return TYPE_BY_ORDINAL[s];
  return "EXTERNAL";
};
const typeMeta = (pt) => TYPE_META[typeKey(pt)];
const typeRank = (pt) => typeMeta(pt).rank;

// Which single day a product appears in a member's dropdown: its start day, or — if it
// began before the stay and is still running — the stay's first day. null = no overlap.
function anchorDay(p, range) {
  if (!range) return null;
  const ps = d10(p.startDate);
  const pe = d10(p.endDate);
  if (!ps || !pe) return null;
  if (pe < range.start || ps > range.end) return null;
  return ps >= range.start ? ps : range.start;
}

function eachDay(start, end) {
  const out = [];
  if (!start || !end) return out;
  let d = dayjs(start);
  const e = dayjs(end);
  let guard = 0;
  while ((d.isSame(e) || d.isBefore(e)) && guard < 400) {
    out.push(d.format("YYYY-MM-DD"));
    d = d.add(1, "day");
    guard += 1;
  }
  return out;
}

// --- per-kind id/booking readers -------------------------------------------------
const adultBookingProductId = (b) =>
  b?.productId ?? b?.product_id ?? b?.product?.id ?? b?.product?.productId ?? null;
const adultBookingId = (b) => b?.id ?? b?.adultBookingId ?? null;
const rawBookingId = (raw, kind) => (kind === "student" ? raw.bookingId : adultBookingId(raw));

async function fetchStudentBookings(studentId, token) {
  const res = await fetch(`${SERVER_URL}api/student/${studentId}/booking`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 204) return [];
  if (!res.ok) throw new Error(`bookings ${studentId} -> ${res.status}`);
  return res.json();
}
async function fetchAdultBookings(adultId, token) {
  const res = await fetch(`${SERVER_URL}api/adults/${adultId}/adult-bookings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 204) return [];
  if (!res.ok) throw new Error(`adult-bookings ${adultId} -> ${res.status}`);
  return res.json();
}

// Normalise a raw student/adult booking to one block shape used for rendering + copy.
function toBlock(raw, kind, productMetaById) {
  if (kind === "student") {
    return {
      kind,
      id: raw.bookingId,
      productId: raw.productId,
      productName: raw.productName,
      productType: raw.productType,
      campus: raw.campus ?? raw.base ?? "",
      actualCharge: raw.actualCharge,
      start: d10(raw.startDate),
      end: d10(raw.endDate),
      raw,
    };
  }
  const productId = adultBookingProductId(raw);
  const pm = productMetaById[productId] || {};
  return {
    kind,
    id: adultBookingId(raw),
    productId,
    productName: raw.productName || raw.name || pm.name || "Product",
    productType: raw.productType ?? pm.productType,
    campus: raw.campus ?? raw.residentialBase ?? pm.campus ?? "",
    actualCharge: raw.actualCharge,
    start: d10(raw.customStart || raw.custom_start || raw.startDate || raw.start_date),
    end: d10(raw.customEnd || raw.custom_end || raw.endDate || raw.end_date),
    raw,
  };
}

// Greedy lane packing: each booking goes in the first lane it doesn't overlap.
function packLanes(blocks) {
  const sorted = [...blocks].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
  const lanes = [];
  for (const b of sorted) {
    let placed = false;
    for (const lane of lanes) {
      if (lane[lane.length - 1].dispEnd < b.dispStart) {
        lane.push(b);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([b]);
  }
  return lanes;
}

// Like packLanes but lets blocks that merely ABUT (one ends the day the next starts)
// share a lane (<= rather than <). Used for residential weeks, which share their
// arrival/departure day and are drawn meeting in a half/half boundary cell.
function packLanesAdjacent(blocks) {
  const sorted = [...blocks].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
  const lanes = [];
  for (const b of sorted) {
    let placed = false;
    for (const lane of lanes) {
      if (lane[lane.length - 1].dispEnd <= b.dispStart) {
        lane.push(b);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([b]);
  }
  return lanes;
}

// Tab 2 of the manual rework — a day-by-day grid of every member's bookings (students and
// adults/staff alike). Days as columns, members as rows; bookings render as blocks spanning
// their days (overlaps stacked into lanes), each with delete/edit; days offer a dropdown.
export default function BookingsTab({ token, showToast, studentIds = [], adultIds = [], group, meta, showFinancials = false }) {
  const [students, setStudents] = useState([]);
  const [adults, setAdults] = useState([]);
  const [recordsByKey, setRecordsByKey] = useState({}); // key -> [{start,end,campus}]
  const [bookingsByKey, setBookingsByKey] = useState({}); // key -> [raw booking]
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  // For an EVENT block: the product behind it, editable (name + dates) from the same
  // dialog. null while loading / for non-event blocks.
  const [editEvent, setEditEvent] = useState(null);
  const [bespokeOpen, setBespokeOpen] = useState(false);
  const [copyDest, setCopyDest] = useState(null); // person being copied INTO
  const [copySource, setCopySource] = useState(null); // person being copied OUT to many

  // Group window/campus, used by the bespoke + copy dialogs (meta dates are Date objects).
  const groupStart = meta?.defaultStartDate ? fmtDate(meta.defaultStartDate) : "";
  const groupEnd = meta?.defaultEndDate ? fmtDate(meta.defaultEndDate) : "";
  const groupCampus = meta?.defaultCampus || group?.campus || "";
  const groupCampusNorm = normCampus(groupCampus);

  const pid = (p) => p.productId ?? p.id;

  const reload = useCallback(async () => {
    if (!studentIds.length && !adultIds.length) {
      setStudents([]);
      setAdults([]);
      setRecordsByKey({});
      setBookingsByKey({});
      return;
    }
    setLoading(true);
    try {
      const [studentPeople, studentStays, adultPeople] = await Promise.all([
        studentIds.length ? getStudentsByIds(studentIds, token) : [],
        studentIds.length ? listStaysForStudents(token, studentIds) : {},
        adultIds.length ? getAdultsByIds(adultIds, token) : [],
      ]);
      setStudents(Array.isArray(studentPeople) ? studentPeople : []);
      setAdults(Array.isArray(adultPeople) ? adultPeople : []);

      // Adult NSRs are per-adult (no batch endpoint) — fetch with light concurrency.
      const nsrsByAdult = {};
      for (let i = 0; i < adultIds.length; i += 6) {
        const chunk = adultIds.slice(i, i + 6);
        const res = await Promise.all(chunk.map((id) => listNsrsForAdult(token, id).catch(() => [])));
        chunk.forEach((id, j) => {
          nsrsByAdult[id] = Array.isArray(res[j]) ? res[j] : [];
        });
      }

      const records = {};
      (studentIds || []).forEach((id) => {
        records[`student:${id}`] = (studentStays[id] || []).map((st) => ({
          start: d10(st.arrivalDate),
          end: d10(st.departureDate),
          campus: st.campus || "",
          // Day (non-residential) stays can't take residential products — the
          // multi-copy check needs to know. Undefined when the feed doesn't say.
          residential: typeof st.residential === "boolean" ? st.residential : undefined,
        }));
      });
      (adultIds || []).forEach((id) => {
        records[`adult:${id}`] = (nsrsByAdult[id] || []).map((n) => ({
          start: d10(n.arrivaldate || n.arrivalDate),
          end: d10(n.departuredate || n.departureDate),
          campus: n.residentialBase || n.residential_base || "",
          residential: typeof n.overnightResident === "boolean" ? n.overnightResident : undefined,
        }));
      });
      setRecordsByKey(records);

      // Bookings: students via /student/{id}/booking, adults via /adults/{id}/adult-bookings.
      const bookings = {};
      const sIds = [...studentIds];
      for (let i = 0; i < sIds.length; i += 8) {
        const chunk = sIds.slice(i, i + 8);
        const results = await Promise.all(chunk.map((id) => fetchStudentBookings(id, token).catch(() => [])));
        chunk.forEach((id, j) => {
          bookings[`student:${id}`] = Array.isArray(results[j]) ? results[j] : [];
        });
      }
      const aIds = [...adultIds];
      for (let i = 0; i < aIds.length; i += 6) {
        const chunk = aIds.slice(i, i + 6);
        const results = await Promise.all(chunk.map((id) => fetchAdultBookings(id, token).catch(() => [])));
        chunk.forEach((id, j) => {
          bookings[`adult:${id}`] = Array.isArray(results[j]) ? results[j] : [];
        });
      }
      setBookingsByKey(bookings);

      // Group-level catalogue: one call resolves the campus/window from the group meta
      // and returns the full bookable menu WITHOUT excluding already-booked products,
      // so the grid can offer a deliberate re-book. Any product type is bookable here;
      // each appears in the dropdown on its anchor day (see anchorDay). 204 = none.
      const groupId = group?.id;
      if (groupId) {
        const res = await fetch(
          `${SERVER_URL}api/products/eligableProductsWide/bookingGroup/${groupId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => null);
        const eligible =
          !res || res.status === 204 ? [] : res.ok ? await res.json().catch(() => []) : [];
        const list = (eligible?.products || eligible || []).filter((p) => p && (p.productId ?? p.id) != null);
        list.sort((a, b) =>
          String(a.productName || a.name).localeCompare(String(b.productName || b.name))
        );
        setProducts(list);
      } else {
        setProducts([]);
      }
    } catch (e) {
      showToast?.("Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  }, [studentIds, adultIds, token, showToast, group?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Unified people list (students first, then adults/staff).
  const people = useMemo(
    () => [
      ...(students || []).map((s) => ({ kind: "student", id: s.id, key: `student:${s.id}`, name: studentName(s) })),
      ...(adults || []).map((a) => ({ kind: "adult", id: a.id, key: `adult:${a.id}`, name: adultName(a) })),
    ],
    [students, adults]
  );

  // productId -> { productType, campus } so we can colour/locate adult bookings (whose
  // payload may not carry the type/campus) from the catalogue.
  const productMetaById = useMemo(() => {
    const m = {};
    products.forEach((p) => {
      const id = p.productId ?? p.id;
      if (id != null) {
        m[id] = {
          productType: p.productType,
          campus: p.base ?? p.campus ?? p.residentialBase ?? "",
          name: p.productName ?? p.name,
        };
      }
    });
    return m;
  }, [products]);

  const blocksByKey = useMemo(() => {
    const out = {};
    people.forEach((p) => {
      out[p.key] = (bookingsByKey[p.key] || []).map((raw) => toBlock(raw, p.kind, productMetaById));
    });
    return out;
  }, [people, bookingsByKey, productMetaById]);

  // Column range = the group window, stretched only by bookings that OVERLAP it (so a
  // residential week running a few days past the window still shows its tail, in grey).
  // A booking entirely outside the window must NOT pull the whole view out to its date —
  // it's simply gated out of the group view. The full stay-duration view is reserved for
  // the individual-booking screen.
  const days = useMemo(() => {
    const hasWindow = Boolean(groupStart && groupEnd);
    let min = groupStart || null;
    let max = groupEnd || null;
    Object.values(blocksByKey || {})
      .flat()
      .forEach((b) => {
        if (!b.start || !b.end) return;
        if (hasWindow && (b.end < groupStart || b.start > groupEnd)) return; // no overlap
        if (!min || b.start < min) min = b.start;
        if (!max || b.end > max) max = b.end;
      });
    if (!min || !max) {
      // No group window and no bookings — fall back to the stay/NSR span.
      Object.values(recordsByKey || {})
        .flat()
        .forEach((r) => {
          if (r.start && (!min || r.start < min)) min = r.start;
          if (r.end && (!max || r.end > max)) max = r.end;
        });
    }
    return min && max ? eachDay(min, max) : [];
  }, [blocksByKey, recordsByKey, groupStart, groupEnd]);

  const rangeFor = (key) => {
    const recs = recordsByKey[key] || [];
    if (!recs.length) return null;
    const a = recs.map((r) => r.start).filter(Boolean).sort()[0];
    const b = recs.map((r) => r.end).filter(Boolean).sort().slice(-1)[0];
    return a && b ? { start: a, end: b } : null;
  };

  const refreshBookingsFor = async (person) => {
    const list =
      person.kind === "student"
        ? await fetchStudentBookings(person.id, token).catch(() => null)
        : await fetchAdultBookings(person.id, token).catch(() => null);
    if (list != null) {
      setBookingsByKey((prev) => ({ ...prev, [person.key]: Array.isArray(list) ? list : [] }));
    }
  };

  const bookOne = async (person, product, range) => {
    setBusy(true);
    try {
      const start = maxStr(d10(product.startDate) || range.start, range.start);
      const end = minStr(d10(product.endDate) || range.end, range.end);
      let res;
      if (person.kind === "student") {
        res = await fetch(
          `${SERVER_URL}api/bookings/studentId/${person.id}/productId/${pid(product)}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              actualCharge: Number(product.defaultRate) || 0,
              bookingStatus: "PROVISIONAL",
              customStart: start,
              customEnd: end,
            }),
          }
        );
      } else {
        // Adults: residential = BOOKING (charged at the default rate); everything else
        // (weekend/activity/event…) = SUPERVISE_ONLY, not charged.
        const isResidential = typeKey(product.productType) === "RESIDENTIAL";
        const body = {
          adultId: person.id,
          participationType: isResidential ? "BOOKING" : "SUPERVISE_ONLY",
          customStart: start,
          customEnd: end,
          countsForTransport: true,
          countsForRatio: false,
          notes: "",
        };
        if (isResidential) body.actualCharge = Number(product.defaultRate) || 0;
        res = await fetch(`${SERVER_URL}api/products/${pid(product)}/adult-bookings`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refreshBookingsFor(person);
    } catch (e) {
      showToast?.("Failed to book", "error");
    } finally {
      setBusy(false);
    }
  };

  // Bespoke event: create a one-off EVENT product (via the dialog) and book it per-head
  // for every student of the group, mirroring the Group finance tab. (Students only.)
  const bookBespokeForGroup = async ({ productId, perHead }) => {
    if (!studentIds.length) return;
    const bookings = studentIds.map((sid) => ({
      studentId: sid,
      productId,
      actualCharge: perHead,
      bookingStatus: "PROVISIONAL",
    }));
    const res = await fetch(`${SERVER_URL}api/bookings/batch-create`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ bookings }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    showToast?.(`Booked the event for ${studentIds.length} student(s)`, "success");
    await Promise.all(people.filter((p) => p.kind === "student").map((p) => refreshBookingsFor(p)));
  };

  // Open the editor: for an EVENT booking also pull the product so its name/date can
  // be changed here — a bespoke event is one product shared by the whole group, so
  // that's where the office fixes "the trip moved to Thursday".
  const openEdit = (b, person) => {
    setEditBooking({
      person,
      kind: person.kind,
      id: b.id,
      productId: b.productId,
      productName: b.productName,
      productType: b.productType,
      actualCharge: b.actualCharge,
      start: b.start,
      end: b.end,
      _raw: b.raw,
    });
    setEditEvent(null);
    if (typeKey(b.productType) !== "EVENT" || b.productId == null) return;
    fetch(`${SERVER_URL}api/products/${b.productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return;
        setEditEvent({
          product: p,
          name: p.name || "",
          start: d10(p.startDate),
          end: d10(p.endDate),
        });
      })
      .catch(() => {});
  };

  // Everyone in the group booked on this product (students + adults), for the
  // "applies to N" note and for re-dating their bookings with the product.
  const bookingsOnProduct = (productId) =>
    people.flatMap((p) =>
      (blocksByKey[p.key] || [])
        .filter((b) => String(b.productId) === String(productId))
        .map((b) => ({ person: p, block: b }))
    );

  // Rename / re-date the event product, then pin every member's booking on it to
  // the new dates. Bookings store IFNULL(custom, product) so a booking whose custom
  // dates were ever set (the dropdown sets them) would otherwise stay on the old day.
  const saveEventProduct = async () => {
    const ev = editEvent;
    if (!ev?.product) return false;
    const p = ev.product;
    const name = ev.name.trim();
    const nameChanged = name && name !== (p.name || "");
    const dateChanged = ev.start !== d10(p.startDate) || ev.end !== d10(p.endDate);
    if (!nameChanged && !dateChanged) return false;
    if (!ev.start || !ev.end || ev.start > ev.end) throw new Error("Event end is before its start");

    const res = await fetch(`${SERVER_URL}api/products/${p.id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...p, name: name || p.name, startDate: ev.start, endDate: ev.end }),
    });
    if (!res.ok) throw new Error(`product ${res.status}`);

    if (dateChanged) {
      const onProduct = bookingsOnProduct(p.id);
      const studentUpdates = onProduct
        .filter((x) => x.person.kind === "student")
        .map((x) => ({ id: x.block.id, customStart: ev.start, customEnd: ev.end }));
      if (studentUpdates.length) {
        const r = await fetch(`${SERVER_URL}api/bookings/batch-update`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ updates: studentUpdates }),
        });
        if (!r.ok) throw new Error(`bookings ${r.status}`);
      }
      for (const x of onProduct.filter((y) => y.person.kind === "adult")) {
        const raw = x.block.raw || {};
        const r = await fetch(`${SERVER_URL}api/adult-bookings/${x.block.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            participationType: raw.participationType || "SUPERVISE_ONLY",
            countsForTransport: raw.countsForTransport ?? true,
            countsForRatio: raw.countsForRatio ?? false,
            notes: raw.notes ?? "",
            customStart: ev.start,
            customEnd: ev.end,
            actualCharge: Number(raw.actualCharge) || 0,
          }),
        });
        if (!r.ok) throw new Error(`adult booking ${r.status}`);
      }
    }
    return true;
  };

  const deleteBooking = async (block, person) => {
    // Optimistic: drop it from the grid immediately (no confirm), restore on failure.
    const prevList = bookingsByKey[person.key] || [];
    setBookingsByKey((prev) => ({
      ...prev,
      [person.key]: (prev[person.key] || []).filter((r) => rawBookingId(r, person.kind) !== block.id),
    }));
    try {
      const url =
        person.kind === "student"
          ? `${SERVER_URL}api/bookings/${block.id}/product/${block.productId}`
          : `${SERVER_URL}api/adult-bookings/${block.id}`;
      const res = await fetch(url, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      setBookingsByKey((prev) => ({ ...prev, [person.key]: prevList }));
      showToast?.("Failed to delete", "error");
    }
  };

  const saveEdit = async () => {
    if (!editBooking) return;
    setBusy(true);
    try {
      const eb = editBooking;
      const isEvent = Boolean(editEvent?.product);
      const eventTouched = await saveEventProduct();
      // An event booking follows the event's dates — its own start/end aren't edited.
      const start = isEvent ? editEvent.start : eb.start;
      const end = isEvent ? editEvent.end : eb.end;
      let res;
      if (eb.kind === "student") {
        res = await fetch(`${SERVER_URL}api/bookings/${eb.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            ...eb._raw,
            actualCharge: Number(eb.actualCharge) || 0,
            customStart: start,
            customEnd: end,
          }),
        });
      } else {
        const r = eb._raw || {};
        res = await fetch(`${SERVER_URL}api/adult-bookings/${eb.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            participationType: r.participationType || "SUPERVISE_ONLY",
            countsForTransport: r.countsForTransport ?? true,
            countsForRatio: r.countsForRatio ?? false,
            notes: r.notes ?? "",
            customStart: start,
            customEnd: end,
            actualCharge: Number(eb.actualCharge) || 0,
          }),
        });
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const person = eb.person;
      setEditBooking(null);
      setEditEvent(null);
      // An event change touches every member's row (and the catalogue) — reload all.
      if (eventTouched) {
        showToast?.("Event updated for everyone booked on it", "success");
        await reload();
      } else {
        await refreshBookingsFor(person);
      }
    } catch (e) {
      showToast?.(`Failed to save${e?.message ? ` (${e.message})` : ""}`, "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading bookings…</Typography>
      </Stack>
    );
  }

  const sameKindCount = (kind) => people.filter((p) => p.kind === kind).length;

  const renderPerson = (person) => {
    const range = rangeFor(person.key);

    const blocks = (blocksByKey[person.key] || [])
      .filter((b) => b.start && b.end)
      .map((b) => ({
        ...b,
        dispStart: maxStr(b.start, days[0]),
        dispEnd: minStr(b.end, days[days.length - 1]),
      }))
      .filter((b) => b.dispStart <= b.dispEnd);
    // Residential on its own rows up top (abutting weeks share a lane via half-cells);
    // everything else packed below.
    const lanes = [
      ...packLanesAdjacent(blocks.filter((b) => typeRank(b.productType) === 0)).map((lane) => ({
        lane,
        isRes: true,
      })),
      ...packLanes(blocks.filter((b) => typeRank(b.productType) !== 0)).map((lane) => ({
        lane,
        isRes: false,
      })),
    ];

    // Colour/state of a block relative to the group: grey = entirely outside the window,
    // red = conflict (wrong campus, or extends beyond the window), else type colour.
    const blockStyle = (b) => {
      const bCampus = b.campus || "";
      const hasWindow = Boolean(groupStart && groupEnd);
      const wrongCampus = bCampus && groupCampusNorm && normCampus(bCampus) !== groupCampusNorm;
      const fullyOutside = hasWindow && (b.end < groupStart || b.start > groupEnd);
      const extendsBeyond = hasWindow && !fullyOutside && (b.start < groupStart || b.end > groupEnd);
      const tm = typeMeta(b.productType);
      if (fullyOutside) {
        return {
          bg: "#eeeeee",
          border: "1px solid #bdbdbd",
          fg: "#757575",
          tip: `Outside the group window (${groupStart} – ${groupEnd})`,
        };
      }
      if (wrongCampus || extendsBeyond) {
        return {
          bg: "#ffcdd2",
          border: "2px solid #d32f2f",
          fg: "#b71c1c",
          tip: wrongCampus
            ? `Wrong campus: ${bCampus} — group is ${groupCampus || "?"}`
            : `Extends beyond the group window (${groupStart} – ${groupEnd})`,
        };
      }
      return {
        bg: tm.color,
        border: `1px solid ${tm.border}`,
        fg: tm.border,
        tip: `${tm.label}${bCampus ? ` · ${bCampus}` : ""}`,
      };
    };

    const blockInner = (b, st) => (
      <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
        <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap", color: st.fg }}>
          {b.productName || "Product"}
        </Typography>
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={() => openEdit(b, person)}
        >
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton size="small" color="error" sx={{ p: 0.25 }} onClick={() => deleteBooking(b, person)}>
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
    );

    // Simple renderer: one colSpan cell per block. Used for non-residential lanes.
    const renderSimpleLane = (lane, laneKey) => {
      const cells = [];
      let i = 0;
      while (i < days.length) {
        const day = days[i];
        const b = lane.find((x) => x.dispStart === day);
        if (b) {
          const span = eachDay(b.dispStart, b.dispEnd).length;
          const st = blockStyle(b);
          cells.push(
            <TableCell
              key={`${laneKey}-${day}`}
              colSpan={span}
              align="center"
              title={st.tip}
              sx={{ bgcolor: st.bg, border: st.border, borderRadius: 1, py: 0.25 }}
            >
              {blockInner(b, st)}
            </TableCell>
          );
          i += span;
        } else {
          cells.push(<TableCell key={`${laneKey}-${day}`} />);
          i += 1;
        }
      }
      return cells;
    };

    // Residential renderer: each week is drawn with HALF-cells on its arrival/departure
    // days, so abutting weeks meet in a single half/half boundary cell on one line
    // instead of stacking. The label + controls live in the full middle cells.
    const renderResLane = (lane, laneKey) => {
      const sorted = [...lane].sort((a, b) => a.dispStart.localeCompare(b.dispStart));
      const idxOf = (dd) => days.indexOf(dd);
      const cells = [];
      let cursor = 0;
      sorted.forEach((b, k) => {
        const sIdx = idxOf(b.dispStart);
        const eIdx = idxOf(b.dispEnd);
        const prev = sorted[k - 1];
        const next = sorted[k + 1];
        const startBoundary = prev && prev.dispEnd === b.dispStart;
        const endBoundary = next && next.dispStart === b.dispEnd;
        const multiDay = eIdx > sIdx;
        const st = blockStyle(b);

        if (!startBoundary) {
          while (cursor < sIdx) {
            cells.push(<TableCell key={`${laneKey}-gap-${cursor}`} />);
            cursor += 1;
          }
        }

        // Start half-cell (right half) on the arrival day, unless that day is already a
        // shared boundary drawn by the previous week.
        let bodyStart = startBoundary ? sIdx + 1 : sIdx;
        if (multiDay && !startBoundary) {
          cells.push(
            <TableCell
              key={`${laneKey}-sh-${k}`}
              title={st.tip}
              sx={{ p: 0, background: `linear-gradient(to right, transparent 50%, ${st.bg} 50%)` }}
            />
          );
          cursor = sIdx + 1;
          bodyStart = sIdx + 1;
        }

        const bodyEnd = multiDay ? eIdx - 1 : eIdx;
        if (bodyStart <= bodyEnd) {
          const span = bodyEnd - bodyStart + 1;
          cells.push(
            <TableCell
              key={`${laneKey}-body-${k}`}
              colSpan={span}
              align="center"
              title={st.tip}
              sx={{ bgcolor: st.bg, py: 0.25 }}
            >
              {blockInner(b, st)}
            </TableCell>
          );
          cursor = bodyEnd + 1;
        } else if (!multiDay && !startBoundary) {
          cells.push(
            <TableCell
              key={`${laneKey}-body-${k}`}
              align="center"
              title={st.tip}
              sx={{ bgcolor: st.bg, py: 0.25 }}
            >
              {blockInner(b, st)}
            </TableCell>
          );
          cursor = sIdx + 1;
        }

        // Departure day: a half/half boundary cell if the next week starts here,
        // otherwise a left-half cell.
        if (multiDay) {
          if (endBoundary) {
            const nextSt = blockStyle(next);
            cells.push(
              <TableCell
                key={`${laneKey}-bd-${k}`}
                title={`${st.tip} → ${nextSt.tip}`}
                sx={{ p: 0, background: `linear-gradient(to right, ${st.bg} 50%, ${nextSt.bg} 50%)` }}
              />
            );
          } else {
            cells.push(
              <TableCell
                key={`${laneKey}-eh-${k}`}
                title={st.tip}
                sx={{ p: 0, background: `linear-gradient(to right, ${st.bg} 50%, transparent 50%)` }}
              />
            );
          }
          cursor = eIdx + 1;
        }
      });
      while (cursor < days.length) {
        cells.push(<TableCell key={`${laneKey}-tail-${cursor}`} />);
        cursor += 1;
      }
      return cells;
    };

    const renderLane = (lane, laneKey, isRes) =>
      isRes ? renderResLane(lane, laneKey) : renderSimpleLane(lane, laneKey);

    const laneRows = lanes.length ? lanes : [{ lane: [], isRes: false }];

    return (
      <React.Fragment key={person.key}>
        {laneRows.map(({ lane, isRes }, li) => (
          <TableRow key={`${person.key}-lane-${li}`}>
            {li === 0 && (
              <TableCell
                rowSpan={laneRows.length + 1}
                sx={{
                  position: "sticky",
                  left: 0,
                  // Light yellow for adults (group leaders/parents travelling with the
                  // group, not our staff) — matches the staff-scheduling amber tint.
                  bgcolor: person.kind === "adult" ? "#fff8e1" : "#fff",
                  verticalAlign: "top",
                }}
              >
                <Stack spacing={0.25} alignItems="flex-start">
                  <span>
                    {person.name}
                    {person.kind === "adult" ? (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.6 }}>
                        · adult
                      </Typography>
                    ) : null}
                  </span>
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setCopyDest(person)}
                      disabled={sameKindCount(person.kind) < 2}
                      sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      Copy from
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CallSplitIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setCopySource(person)}
                      disabled={sameKindCount(person.kind) < 2 || !blocks.length}
                      title="Push this member's bookings out to others in the group"
                      sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: 11, whiteSpace: "nowrap" }}
                    >
                      Copy to…
                    </Button>
                  </Stack>
                </Stack>
              </TableCell>
            )}
            {renderLane(lane, `${person.key}-${li}`, isRes)}
          </TableRow>
        ))}
        {/* Add row — a product is offered on its anchor day (start day, or stay start if
            it began earlier and is still running). */}
        <TableRow key={`${person.key}-add`}>
          {days.map((day) => {
            const dayProducts = range ? products.filter((p) => anchorDay(p, range) === day) : [];
            if (!dayProducts.length) {
              return <TableCell key={`add-${person.key}-${day}`} />;
            }
            return (
              <TableCell key={`add-${person.key}-${day}`} align="center" sx={{ p: 0.25 }}>
                <FormControl size="small" sx={{ minWidth: 44 }}>
                  <Select
                    value=""
                    displayEmpty
                    disabled={busy}
                    onChange={(e) => {
                      const prod = dayProducts.find((p) => String(pid(p)) === String(e.target.value));
                      if (prod) bookOne(person, prod, range);
                    }}
                    sx={{ "& .MuiSelect-select": { py: 0.25, fontSize: 12 } }}
                    renderValue={() => "+"}
                  >
                    {dayProducts.map((p) => (
                      <MenuItem key={pid(p)} value={String(pid(p))}>
                        {p.productName || p.name} ({d10(p.startDate)}→{d10(p.endDate)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            );
          })}
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ p: 1 }}>
      {showFinancials && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setBespokeOpen(true)}
            disabled={!studentIds.length}
          >
            Add bespoke event
          </Button>
        </Stack>
      )}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          Every member&apos;s bookings by day — students and staff. Blocks span the days they
          cover; overlapping products stack (residential on top, then everything else).
          Trash/edit a block to remove or adjust it; a product is offered in the dropdown on
          the day it starts (or the stay&apos;s first day if it began earlier and is still
          running), booked with its dates clamped to the stay. Columns are the group window;
          grey = a booking outside it, red = a conflict (wrong campus, or extends beyond the
          window).
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}>
          {LEGEND_ORDER.map((k) => (
            <Stack key={k} direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: TYPE_META[k].color,
                  border: `1px solid ${TYPE_META[k].border}`,
                }}
              />
              <Typography variant="caption">{TYPE_META[k].label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {days.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No stays — add stays/NSRs on the Stays tab first.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ "& td, & th": { borderRight: "1px solid #eee", px: 0.5 } }}>
            <TableHead>
              <TableRow>
                {/* Wide enough for "Copy from · Copy to…" on one line. */}
                <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#fff", minWidth: 200 }}>
                  Member
                </TableCell>
                {days.map((day) => {
                  const outside = groupStart && groupEnd && (day < groupStart || day > groupEnd);
                  return (
                    <TableCell
                      key={day}
                      align="center"
                      sx={{ minWidth: 56, bgcolor: outside ? "#f5f5f5" : undefined }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: "nowrap", color: outside ? "text.disabled" : undefined }}
                      >
                        {dayjs(day).format(" dd D")}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>{people.map((person) => renderPerson(person))}</TableBody>
          </Table>
        </Box>
      )}

      <Dialog
        open={Boolean(editBooking)}
        onClose={() => {
          setEditBooking(null);
          setEditEvent(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit {editBooking?.productName || "booking"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {editBooking && typeKey(editBooking.productType) === "EVENT" && (
              editEvent ? (
                <>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Event details — shared by everyone booked on it (
                    {bookingsOnProduct(editBooking.productId).length} in this group). Changing the
                    date moves all of their bookings.
                  </Typography>
                  <TextField
                    label="Event name"
                    size="small"
                    value={editEvent.name}
                    onChange={(e) => setEditEvent((ev) => ({ ...ev, name: e.target.value }))}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Event date"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editEvent.start}
                      onChange={(e) =>
                        setEditEvent((ev) => ({
                          ...ev,
                          start: e.target.value,
                          // A one-day event stays one day when its date moves.
                          end: ev.start === ev.end ? e.target.value : ev.end,
                        }))
                      }
                    />
                    <TextField
                      label="Event end"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editEvent.end}
                      onChange={(e) => setEditEvent((ev) => ({ ...ev, end: e.target.value }))}
                    />
                  </Stack>
                  {groupStart && groupEnd && (editEvent.start < groupStart || editEvent.end > groupEnd) && (
                    <Typography variant="caption" color="warning.main">
                      Outside the group window ({groupStart} – {groupEnd}).
                    </Typography>
                  )}
                </>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography variant="caption">Loading event…</Typography>
                </Stack>
              )
            )}
            {showFinancials && (
              <TextField
                label={
                  editBooking && typeKey(editBooking.productType) === "EVENT"
                    ? "Charge £ (this member only)"
                    : "Charge £"
                }
                type="number"
                size="small"
                value={editBooking?.actualCharge ?? ""}
                onChange={(e) => setEditBooking((b) => ({ ...b, actualCharge: e.target.value }))}
              />
            )}
            {!(editBooking && typeKey(editBooking.productType) === "EVENT") && (
              <>
                <TextField
                  label="Start"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={editBooking?.start || ""}
                  onChange={(e) => setEditBooking((b) => ({ ...b, start: e.target.value }))}
                />
                <TextField
                  label="End"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={editBooking?.end || ""}
                  onChange={(e) => setEditBooking((b) => ({ ...b, end: e.target.value }))}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditBooking(null);
              setEditEvent(null);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={busy || (editBooking && typeKey(editBooking.productType) === "EVENT" && !editEvent)}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <BespokeEventDialog
        open={bespokeOpen}
        onClose={() => setBespokeOpen(false)}
        defaultCampus={groupCampus}
        windowStart={groupStart}
        windowEnd={groupEnd}
        onBook={bookBespokeForGroup}
      />

      <CopyBookingsDialog
        open={copyDest != null}
        onClose={() => setCopyDest(null)}
        token={token}
        showToast={showToast}
        dest={copyDest}
        candidates={copyDest ? people.filter((p) => p.kind === copyDest.kind && p.key !== copyDest.key) : []}
        blocksByKey={blocksByKey}
        recordsByKey={recordsByKey}
        groupStart={groupStart}
        groupEnd={groupEnd}
        groupCampus={groupCampus}
        showFinancials={showFinancials}
        onApplied={refreshBookingsFor}
      />

      <MultiCopyBookingsDialog
        open={copySource != null}
        onClose={() => setCopySource(null)}
        token={token}
        showToast={showToast}
        source={copySource}
        candidates={
          copySource ? people.filter((p) => p.kind === copySource.kind && p.key !== copySource.key) : []
        }
        blocksByKey={blocksByKey}
        recordsByKey={recordsByKey}
        groupStart={groupStart}
        groupEnd={groupEnd}
        groupCampus={groupCampus}
        showFinancials={showFinancials}
        onApplied={async (persons) => {
          for (let i = 0; i < persons.length; i += 6) {
            await Promise.all(persons.slice(i, i + 6).map((p) => refreshBookingsFor(p)));
          }
        }}
      />
    </Box>
  );
}
